import { useCallback, useEffect, useState } from "react";

// Durable user preferences that apply across all trips (dietary,
// mobility, companions, taste). Persisted to localStorage and prepended
// to the concierge system prompt server-side. Lives outside JourneyContext
// because it isn't per-trip.

const STORAGE_KEY = "sundayatlas_preferences";

const readFromStorage = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
};

const writeToStorage = (value: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage may be full or disabled. Silently ignore.
  }
};

export function usePreferences(): {
  preferences: string;
  setPreferences: (value: string) => void;
} {
  const [preferences, setPreferencesState] = useState<string>(() =>
    readFromStorage()
  );

  // Cross-tab sync: if another tab changes the value, reflect it here.
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setPreferencesState(e.newValue ?? "");
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const setPreferences = useCallback((value: string) => {
    setPreferencesState(value);
    writeToStorage(value);
  }, []);

  return { preferences, setPreferences };
}
